import sharedConfig from "../../shared-config.json";

// Screening-call booking API.
//
// Deliberately plain fetch rather than utilities/postData: postData
// retries POSTs and caches failures in localStorage for later replay,
// which is wrong for bookings (a retried timeout could book twice).
// `cache: "no-store"` because the Cloudflare proxy marks successful
// GETs as publicly cacheable for 60s.
//
// `referrerPolicy: "no-referrer"`: /book?t=… carries the manage token in
// its URL, and the proxy forwards request headers to the API, so no
// screening request sends a Referer.
export const NO_REFERRER = "no-referrer";

async function call(path, { method = "POST", body, token } = {}) {
  const headers = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  let res;
  try {
    res = await fetch(`/api/${path}`, {
      method,
      headers,
      cache: "no-store",
      referrerPolicy: NO_REFERRER,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw { status: 0, error: "Couldn't reach the server. Check your connection and try again." };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw { status: res.status, ...data, error: data.error || data.message || `Error ${res.status}` };
  return data;
}

// Public (no login) — participant booking page. `link` is the identity
// from the URL: { eoi, k } (key echoed back by REDCap) or { eoi, sig }.
export const getAvailability = (link) => call("screening/availability", { body: link });
export const bookSlot = (link, { start, end }) => call("screening/book", { body: { ...link, start, end } });

// Both requests on the way to the EOI form give up after this long:
// registering interest must never wait on the API.
const INTEREST_TIMEOUT_MS = 3000;

async function fetchWithin(ms, url, options = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Send the visitor to the EOI form, with a booking key and any utm_ values
 * (from utmParams). For "Express Interest" buttons on pages that haven't
 * already loaded the study config (Home has its own handler), and for
 * /interest, which passes `replace` so that Back from the form doesn't
 * return to /interest and send the visitor on again. Falls back to the
 * build-time EOI URL, also when the study config takes more than 3s.
 */
export async function goToInterestForm(utm, { replace = false } = {}) {
  let eoiUrl = sharedConfig.urls?.eoi;
  try {
    const res = await fetchWithin(INTEREST_TIMEOUT_MS, "/api/public-study-config", {
      cache: "no-store",
      referrerPolicy: NO_REFERRER,
    });
    if (res.ok) {
      const cfg = await res.json();
      if (cfg?.eoi_url) eoiUrl = cfg.eoi_url;
    }
  } catch {
    // keep the fallback
  }
  if (!eoiUrl) return;
  const address = await withBookingKey(eoiUrl, utm);
  if (replace) window.location.replace(address);
  else window.location.href = address;
}

/**
 * Add a fresh signed booking key to the EOI form URL, as the prefill for
 * the hidden REDCap field. Gives up after 3s and returns the URL without a
 * key, since registering interest must never depend on this. The utm_
 * values go in before the key is requested, so they are kept either way.
 */
export async function withBookingKey(eoiUrl, utm) {
  const address = withUtm(eoiUrl, utm);
  try {
    const res = await fetchWithin(INTEREST_TIMEOUT_MS, "/api/screening/key", {
      method: "POST",
      cache: "no-store",
      referrerPolicy: NO_REFERRER,
    });
    if (!res.ok) return address;
    const { key, field } = await res.json();
    if (!key || !field) return address;
    const url = new URL(address);
    url.searchParams.set(field, key);
    return url.toString();
  } catch {
    return address;
  }
}

const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

/**
 * The five utm_ values in a query string such as `location.search`, and
 * nothing else from it: no other parameter on the page (the manage token
 * on /book, an advert's click ID) may reach the EOI form.
 */
export function utmParams(search) {
  const params = new URLSearchParams(search);
  const utm = {};
  for (const name of UTM_PARAMS) {
    const value = params.get(name);
    if (value) utm[name] = value;
  }
  return utm;
}

/**
 * The EOI form URL with the utm_ values added. Without any, the URL is
 * returned exactly as given, so calls that pass none are unchanged.
 */
export function withUtm(eoiUrl, utm) {
  const names = UTM_PARAMS.filter((name) => utm?.[name]);
  if (!names.length) return eoiUrl;
  try {
    const url = new URL(eoiUrl);
    for (const name of names) url.searchParams.set(name, utm[name]);
    return url.toString();
  } catch {
    return eoiUrl;
  }
}
export const getBooking = (token) => call("screening/manage", { body: { token } });
// `currentStart` is the time the page shows: if the booking has moved
// since, the API refuses (BOOKING_CHANGED) rather than act on it.
export const cancelBooking = (token, currentStart) => call("screening/cancel", { body: { token, currentStart } });
export const rescheduleBooking = (token, { start, end }, currentStart) =>
  call("screening/reschedule", { body: { token, start, end, currentStart } });

// Staff (Admin / PI) — availability management.
// A POST, so the proxy never marks it cacheable: it carries EOI IDs and
// video meeting links.
export const adminListSlots = (token, { from, to } = {}) =>
  call("screening-admin/list", { body: { from, to }, token });
export const adminAddAvailability = (token, block) => call("screening-admin/slots", { body: block, token });
export const adminDeleteSlot = (token, slotId) => call("screening-admin/delete-slot", { body: { slotId }, token });
// Team changes (plan 7).
export const adminCancelBooking = (token, bookingId, { reason, reopen } = {}) =>
  call("screening-admin/cancel-booking", { body: { bookingId, reason, reopen }, token });
export const adminReassign = (token, bookingId, slotId, { reopen } = {}) =>
  call("screening-admin/reassign", { body: { bookingId, slotId, reopen }, token });
export const adminMove = (token, bookingId, slotId, { reopen } = {}) =>
  call("screening-admin/move", { body: { bookingId, slotId, reopen }, token });
export const adminRemoveSlots = (token, { from, to, screenerId }) =>
  call("screening-admin/remove-slots", { body: { from, to, screenerId }, token });
// Staff booking: { eoiId, slotId } or { eoiId, date, time, screenerId }.
export const adminBook = (token, booking) => call("screening-admin/book", { body: booking, token });
export const adminBookSlot = (token, slotId, eoiId) => adminBook(token, { slotId, eoiId });
export const adminReissueLink = (token, bookingId) => call("screening-admin/reissue-link", { body: { bookingId }, token });
// After the call (plan 8).
// `reason` is only "link_mismatch", with "cancelled_team".
export const adminSetStatus = (token, bookingId, status, reason) =>
  call("screening-admin/set-status", { body: { bookingId, status, reason }, token });
export const adminRebookLink = (token, bookingId) => call("screening-admin/rebook-link", { body: { bookingId }, token });
export const adminScreeners = (token) => call("screening-admin/screeners", { body: {}, token });
export const adminImportPreview = (token, rows) => call("screening-admin/import-preview", { body: { rows }, token });
export const adminImport = (token, rows) => call("screening-admin/import", { body: { rows }, token });
export const adminSignLinks = (token, eoiIds) => call("screening-admin/links", { body: { eoiIds }, token });
export const adminResendEmail = (token, bookingId, notificationId) =>
  call("screening-admin/resend-email", { body: { bookingId, notificationId }, token });
// A POST, like the list: the settings name staff and the shared inbox.
export const adminGetSettings = (token) => call("screening-admin/read-settings", { body: {}, token });
export const adminSaveSettings = (token, settings) => call("screening-admin/settings", { body: settings, token });

// ── UK-time display helpers (shared by both pages) ───────────────────

export const TIME_ZONE = "Europe/London";

export const londonDateKey = (iso) =>
  new Intl.DateTimeFormat("en-CA", { timeZone: TIME_ZONE, year: "numeric", month: "2-digit", day: "2-digit" }).format(
    new Date(iso),
  );

export const formatDay = (iso) =>
  new Intl.DateTimeFormat("en-GB", { timeZone: TIME_ZONE, weekday: "long", day: "numeric", month: "long" }).format(
    new Date(iso),
  );

export const formatTime = (iso) =>
  new Intl.DateTimeFormat("en-GB", { timeZone: TIME_ZONE, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(
    new Date(iso),
  );

export const formatRange = (start, end) => `${formatDay(start)}, ${formatTime(start)}–${formatTime(end)}`;

/** Group [{start, end, ...}] by London calendar day, preserving order. */
export function groupByDay(items) {
  const groups = new Map();
  for (const item of items) {
    const key = londonDateKey(item.start);
    if (!groups.has(key)) groups.set(key, { key, label: formatDay(item.start), items: [] });
    groups.get(key).items.push(item);
  }
  return [...groups.values()];
}

// ── Participant calendar entry ───────────────────────────────────────
//
// We hold no contact details, so the participant's own calendar entry is
// the only place their booking lives on their side. It carries the call
// link (video) and the link to change or cancel, and its reminders stand
// in for reminder emails.

const icsDate = (iso) => new Date(iso).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
const icsEscape = (s) => String(s || "").replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n");

/** { title, description, location } for a booking, shared by every calendar route. */
export function participantEvent({ booking, manageUrl, instructions }) {
  const video = booking.callType === "video" && booking.meetingUrl;
  const lines = ["Your PRADAM screening call with the study team."];
  if (video) lines.push("", `Join the video call: ${booking.meetingUrl}`);
  else lines.push("", "The study team will phone you on the number you gave on your interest form.");
  if (instructions) lines.push("", instructions);
  lines.push("", `Need to change or cancel? ${manageUrl}`);
  return {
    title: "PRADAM screening call",
    description: lines.join("\n"),
    location: video ? booking.meetingUrl : "Phone call",
    url: video ? booking.meetingUrl : manageUrl,
  };
}

/**
 * UID and SEQUENCE for the participant's entry. The server sends one UID
 * per booking and a SEQUENCE that rises with every change, so re-adding
 * after a change updates the entry in calendars that honour it (Apple,
 * Outlook desktop). Bookings made before that keep their original UID
 * with a time-based SEQUENCE.
 */
export function participantCalendarIdentity(booking, now = Date.now()) {
  const cal = booking.calendar;
  if (cal && cal.uid && Number.isInteger(cal.sequence)) return { uid: cal.uid, sequence: cal.sequence };
  return {
    uid: (cal && cal.uid) || `pradam-screening-${booking.eoiId}@pradam.org`,
    sequence: Math.floor(now / 1000),
  };
}

export function buildParticipantIcs({ booking, event }) {
  const { uid, sequence } = participantCalendarIdentity(booking);
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Delosis//PRADAM Screening//EN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `SEQUENCE:${sequence}`,
    `DTSTAMP:${icsDate(new Date().toISOString())}`,
    `DTSTART:${icsDate(booking.start)}`,
    `DTEND:${icsDate(booking.end)}`,
    `SUMMARY:${icsEscape(event.title)}`,
    `DESCRIPTION:${icsEscape(event.description)}`,
    `LOCATION:${icsEscape(event.location)}`,
    `URL:${event.url}`,
    "BEGIN:VALARM",
    "ACTION:DISPLAY",
    `DESCRIPTION:${icsEscape(event.title)}`,
    "TRIGGER:-P1D",
    "END:VALARM",
    "BEGIN:VALARM",
    "ACTION:DISPLAY",
    `DESCRIPTION:${icsEscape(event.title)}`,
    "TRIGGER:-PT30M",
    "END:VALARM",
    "END:VEVENT",
    "END:VCALENDAR",
    "",
  ].join("\r\n");
}

export function googleCalendarUrl({ booking, event }) {
  const qs = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title,
    dates: `${icsDate(booking.start)}/${icsDate(booking.end)}`,
    details: event.description,
    location: event.location,
  });
  return `https://calendar.google.com/calendar/render?${qs}`;
}

function outlookComposeQuery({ booking, event }) {
  return new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: event.title,
    startdt: new Date(booking.start).toISOString(),
    enddt: new Date(booking.end).toISOString(),
    body: event.description,
    location: event.location,
  });
}

// Outlook.com: personal Microsoft accounts.
export function outlookCalendarUrl({ booking, event }) {
  return `https://outlook.live.com/calendar/0/deeplink/compose?${outlookComposeQuery({ booking, event })}`;
}

// Microsoft 365: work and school accounts.
export function office365CalendarUrl({ booking, event }) {
  return `https://outlook.office.com/calendar/0/deeplink/compose?${outlookComposeQuery({ booking, event })}`;
}

export function downloadIcs(filename, content) {
  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ── CSV (plan 7.6 import) ────────────────────────────────────────────

/** Parse CSV text (RFC 4180: quoted fields, "" escapes, CRLF or LF). */
export function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') {
        field += '"';
        i++;
      } else if (ch === '"') {
        quoted = false;
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ",") {
      row.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += ch;
    }
  }
  if (field !== "" || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((cell) => cell.trim() !== ""));
}

export const IMPORT_COLUMNS = ["eoi_id", "start", "screener_email"];

/**
 * The import rows from a CSV: only eoi_id, start and screener_email are
 * kept, whatever else the file has (a Calendly export can hold contact
 * details and answers, which never leave the browser).
 * Returns { rows, missing } — `missing` lists required columns not found.
 */
export function importRowsFromCsv(text) {
  const [header = [], ...data] = parseCsv(text.replace(/^\uFEFF/, ""));
  const index = Object.fromEntries(header.map((name, i) => [name.trim().toLowerCase(), i]));
  const missing = IMPORT_COLUMNS.filter((name) => !(name in index));
  if (missing.length) return { rows: [], missing };
  const rows = data.map((cells) =>
    Object.fromEntries(IMPORT_COLUMNS.map((name) => [name, (cells[index[name]] || "").trim()])),
  );
  return { rows, missing };
}

const csvCell = (value) => (/[",\n\r]/.test(value) ? `"${String(value).replace(/"/g, '""')}"` : String(value));

/** CSV of EOI IDs and manage links, for REDCap import. */
export function manageLinksCsv(created, origin, { idField = "record_id", linkField = "screening_manage_link" } = {}) {
  const lines = [[idField, linkField], ...created.map((c) => [c.eoiId, `${origin}/book?t=${encodeURIComponent(c.manageToken)}`])];
  return lines.map((cells) => cells.map(csvCell).join(",")).join("\n") + "\n";
}

